/**
 * Recorte de análisis alineado con `object-fit: cover` + `object-position` del preview.
 * Mapeo canvas/viewport → sensor para enfoque de cámara y análisis (OCR/POL/DOCs, NIF-352).
 */

export type VideoObjectPosition = 'top' | 'center';

/** Alineado con preview centrado en sensor (OCR/POL/DOCs). */
export const DEFAULT_VIDEO_OBJECT_POSITION: VideoObjectPosition = 'center';

export interface VideoCoverSourceRect {
	sx: number;
	sy: number;
	sw: number;
	sh: number;
}

/** @deprecated Usar `VideoCoverSourceRect`. */
export type PolCoverSourceRect = VideoCoverSourceRect;

export interface INormalizedSensorPoint {
	x: number;
	y: number;
}

/** Rectángulo fuente visible con object-fit: cover (viewport = aspecto del <video> en pantalla). */
export function getObjectFitCoverSourceRect(
	videoWidth: number,
	videoHeight: number,
	viewportAspect: number,
	objectPosition: VideoObjectPosition = DEFAULT_VIDEO_OBJECT_POSITION,
): VideoCoverSourceRect {
	if (!videoWidth || !videoHeight || !viewportAspect || viewportAspect <= 0) {
		return { sx: 0, sy: 0, sw: videoWidth, sh: videoHeight };
	}

	const videoAspect = videoWidth / videoHeight;
	let sw: number;
	let sh: number;
	let sx: number;
	let sy: number;

	if (videoAspect > viewportAspect) {
		sh = videoHeight;
		sw = videoHeight * viewportAspect;
		sx = (videoWidth - sw) / 2;
		sy = 0;
	} else {
		sw = videoWidth;
		sh = videoWidth / viewportAspect;
		sx = 0;
		sy = objectPosition === 'center' ? (videoHeight - sh) / 2 : 0;
	}

	return { sx, sy, sw, sh };
}

/** Centro del rectángulo cover en coordenadas normalizadas del sensor (0–1). */
export function getCoverRegionCenterNormalized(
	videoWidth: number,
	videoHeight: number,
	viewportAspect: number,
	objectPosition: VideoObjectPosition = DEFAULT_VIDEO_OBJECT_POSITION,
): INormalizedSensorPoint {
	if (!videoWidth || !videoHeight) {
		return { x: 0.5, y: 0.5 };
	}
	const cover = getObjectFitCoverSourceRect(videoWidth, videoHeight, viewportAspect, objectPosition);
	return {
		x: clamp01((cover.sx + cover.sw / 2) / videoWidth),
		y: clamp01((cover.sy + cover.sh / 2) / videoHeight),
	};
}

/** Punto del canvas de análisis (mismo recorte que drawImage) → sensor normalizado. */
export function mapCanvasPointToSensorNormalized(
	px: number,
	py: number,
	canvasWidth: number,
	canvasHeight: number,
	cover: VideoCoverSourceRect,
	videoWidth: number,
	videoHeight: number,
): INormalizedSensorPoint {
	if (!canvasWidth || !canvasHeight || !videoWidth || !videoHeight) {
		return { x: 0.5, y: 0.5 };
	}
	const sensorX = cover.sx + (px / canvasWidth) * cover.sw;
	const sensorY = cover.sy + (py / canvasHeight) * cover.sh;
	return {
		x: clamp01(sensorX / videoWidth),
		y: clamp01(sensorY / videoHeight),
	};
}

/** Tap/click en el elemento <video> (0–1) → sensor normalizado. */
export function mapViewportPointToSensorNormalized(
	relX: number,
	relY: number,
	videoWidth: number,
	videoHeight: number,
	viewportAspect: number,
	objectPosition: VideoObjectPosition = DEFAULT_VIDEO_OBJECT_POSITION,
): INormalizedSensorPoint {
	if (!videoWidth || !videoHeight) {
		return { x: clamp01(relX), y: clamp01(relY) };
	}
	const cover = getObjectFitCoverSourceRect(videoWidth, videoHeight, viewportAspect, objectPosition);
	return mapCanvasPointToSensorNormalized(
		relX * cover.sw,
		relY * cover.sh,
		cover.sw,
		cover.sh,
		cover,
		videoWidth,
		videoHeight,
	);
}

/** Recorte central adicional (factor > 1 acerca; factor < 1 aleja el encuadre de análisis). */
export function applyCenterZoomToSourceRect(
	rect: VideoCoverSourceRect,
	zoomFactor: number,
	videoWidth?: number,
	videoHeight?: number,
): VideoCoverSourceRect {
	if (!zoomFactor || zoomFactor === 1) {
		return { ...rect };
	}

	if (zoomFactor > 1) {
		const sw = rect.sw / zoomFactor;
		const sh = rect.sh / zoomFactor;
		return {
			sx: rect.sx + (rect.sw - sw) / 2,
			sy: rect.sy + (rect.sh - sh) / 2,
			sw,
			sh,
		};
	}

	const sw = rect.sw / zoomFactor;
	const sh = rect.sh / zoomFactor;
	let sx = rect.sx + (rect.sw - sw) / 2;
	let sy = rect.sy + (rect.sh - sh) / 2;

	if (videoWidth != null && videoHeight != null && videoWidth > 0 && videoHeight > 0) {
		const clampedSw = Math.min(sw, videoWidth);
		const clampedSh = Math.min(sh, videoHeight);
		sx = Math.max(0, sx + (sw - clampedSw) / 2);
		sy = Math.max(0, sy + (sh - clampedSh) / 2);
		sx = Math.min(sx, videoWidth - clampedSw);
		sy = Math.min(sy, videoHeight - clampedSh);
		return { sx, sy, sw: clampedSw, sh: clampedSh };
	}

	return { sx, sy, sw, sh };
}

export function resolveVideoCoverSourceRect(
	videoWidth: number,
	videoHeight: number,
	viewportWidth: number,
	viewportHeight: number,
	extraZoomFactor: number = 1,
	objectPosition: VideoObjectPosition = DEFAULT_VIDEO_OBJECT_POSITION,
): VideoCoverSourceRect {
	const viewportAspect =
		viewportWidth > 0 && viewportHeight > 0 ? viewportWidth / viewportHeight : videoWidth / videoHeight;
	const cover = getObjectFitCoverSourceRect(videoWidth, videoHeight, viewportAspect, objectPosition);
	return applyCenterZoomToSourceRect(cover, extraZoomFactor, videoWidth, videoHeight);
}

/** @deprecated Usar `resolveVideoCoverSourceRect`. */
export const resolvePolAnalysisSourceRect = resolveVideoCoverSourceRect;

/** Pinta en el canvas el frame visible del preview (mismo aspecto que el recorte cover + zoom). */
export function drawVideoCoverFrameToCanvas(
	video: HTMLVideoElement,
	canvas: HTMLCanvasElement,
	ctx: CanvasRenderingContext2D,
	viewportWidth: number,
	viewportHeight: number,
	extraZoomFactor: number = 1,
	objectPosition: VideoObjectPosition = DEFAULT_VIDEO_OBJECT_POSITION,
): void {
	const vw = video.videoWidth;
	const vh = video.videoHeight;
	if (!vw || !vh) return;

	const { sx, sy, sw, sh } = resolveVideoCoverSourceRect(
		vw,
		vh,
		viewportWidth,
		viewportHeight,
		extraZoomFactor,
		objectPosition,
	);
	const cw = Math.max(1, Math.round(sw));
	const ch = Math.max(1, Math.round(sh));

	if (canvas.width !== cw) canvas.width = cw;
	if (canvas.height !== ch) canvas.height = ch;

	ctx.drawImage(video, sx, sy, sw, sh, 0, 0, cw, ch);
}

/** @deprecated Usar `drawVideoCoverFrameToCanvas`. */
export const drawPolAnalysisFrameToCanvas = drawVideoCoverFrameToCanvas;

function clamp01(value: number): number {
	return Math.min(1, Math.max(0, value));
}
